import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const SupportChat = () => {
    const { t } = useLanguage();
    const location = useLocation();
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [chatMode, setChatMode] = useState(null); // null, 'live', 'whatsapp'
    const [chatMsg, setChatMsg] = useState('');
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // Auto-scroll to bottom when new messages arrive
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Focus input when chat mode is selected
    useEffect(() => {
        if (chatMode && inputRef.current) {
            inputRef.current.focus();
        }
    }, [chatMode]);

    // Show tooltip after 5 seconds if chat is closed
    useEffect(() => {
        const timer = setTimeout(() => {
            if (!isChatOpen) setShowTooltip(true);
        }, 5000);
        return () => clearTimeout(timer);
    }, [isChatOpen]);

    // Optimization: Don't render in iframes (except when specifically allowed)
    if (window.self !== window.top) return null;

    // Check if on auth pages or mobile customer ordering page where it might interfere
    const isAuthPage = location.pathname === '/signup' || location.pathname === '/login' || location.pathname === '/staff';
    const isCustomerPage = location.pathname.includes('/customer.html');
    
    // Check if explicitly on a subdomain (customer view)
    // const hostname = window.location.hostname;

    // Hide only on specific pages where it's definitely not wanted
    if (isAuthPage || isCustomerPage) return null;

    // Optional: Hide on subdomain customer pages if strictly required
    // if (isSubdomain) return null;

    // Handle Live Chat (RAG/LLM)
    const handleLiveChat = async (e) => {
        e.preventDefault();
        if (!chatMsg.trim() || isLoading) return;

        const userMessage = chatMsg.trim();
        
        // Add user message to chat
        const newUserMsg = {
            id: Date.now(),
            type: 'user',
            text: userMessage,
            timestamp: new Date()
        };
        setMessages(prev => [...prev, newUserMsg]);
        setChatMsg('');
        setIsLoading(true);

        try {
            // Call RAG API
            const response = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/rag-chat`, {
                message: userMessage
            });

            if (response.data.success) {
                // Add AI response to chat
                const aiMessage = {
                    id: Date.now() + 1,
                    type: 'ai',
                    text: response.data.reply,
                    sources: response.data.sources || [],
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, aiMessage]);
            } else {
                throw new Error(response.data.error || 'Failed to get response');
            }
        } catch (error) {
            console.error('Live chat error:', error);
            // Add error message
            const errorMessage = {
                id: Date.now() + 1,
                type: 'ai',
                text: 'I apologize, but I\'m having trouble connecting right now. Please try again in a moment, or feel free to reach out via WhatsApp for immediate assistance! 😊',
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle WhatsApp redirect (existing functionality)
    const handleWhatsAppChat = (e) => {
        e.preventDefault();
        if (!chatMsg.trim()) return;

        const encodedMsg = encodeURIComponent(chatMsg);
        window.open(`https://wa.me/917780321490?text=${encodedMsg}`, '_blank');
        setChatMsg('');
    };

    // Reset chat
    const resetChat = () => {
        setChatMode(null);
        setMessages([]);
        setChatMsg('');
        setIsLoading(false);
    };

    // Format timestamp
    const formatTime = (date) => {
        return new Date(date).toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };


    return (
        <div className="fixed bottom-6 right-6 z-[99999] flex flex-col items-end md:items-end items-center" data-component="support-chat-v2">
            {/* Chat Frame */}
            {isChatOpen && (
                <div className="mb-4 w-[calc(100vw-3rem)] max-w-[380px] sm:max-w-[450px] h-[600px] bg-white rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up border border-gray-100 flex flex-col fixed md:relative bottom-20 md:bottom-0 left-1/2 md:left-auto -translate-x-1/2 md:translate-x-0">
                    {/* Frame Header - Premium Indigo/Purple Gradient */}
                    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 text-white flex items-center justify-between shadow-lg">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                                <i className="fab fa-whatsapp text-xl"></i>
                            </div>
                            <div className="text-left">
                                <p className="font-bold text-sm leading-tight">Support Chat</p>
                                <p className="text-[10px] opacity-80 leading-tight mt-0.5">Online • Replies in minutes</p>
                            </div>
                        </div>
                        <button onClick={() => setIsChatOpen(false)} className="hover:bg-white/20 p-1.5 rounded-full transition-colors">
                            <i className="fas fa-times"></i>
                        </button>
                    </div>

                    {/* Frame Body */}
                    <div className="flex-1 bg-[#f8fafc] relative overflow-hidden flex flex-col">
                        {/* Subtle Pattern Background */}
                        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded51.png")' }}></div>

                        {/* Mode Selection Screen */}
                        {!chatMode && (
                            <div className="relative z-10 flex-1 p-6 flex flex-col items-center justify-center gap-5">
                                <div className="text-center mb-2">
                                    <h3 className="text-xl font-bold text-gray-800 mb-2">Choose Support Type</h3>
                                    <p className="text-sm text-gray-500">Select how you'd like to get help</p>
                                </div>

                                {/* Live Chat Option */}
                                <button
                                    onClick={() => {
                                        setChatMode('live');
                                        setMessages([{
                                            id: Date.now(),
                                            type: 'ai',
                                            text: 'Hi there! 👋 How can I help you with RedSorm today?',
                                            timestamp: new Date()
                                        }]);
                                    }}
                                    className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-5 rounded-xl hover:shadow-lg transition-all transform hover:scale-[1.02] flex items-center gap-4"
                                >
                                    <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                                        <i className="fas fa-robot text-2xl"></i>
                                    </div>
                                    <div className="text-left flex-1">
                                        <p className="font-bold text-base">Live Chat (AI)</p>
                                        <p className="text-xs opacity-90 mt-0.5">Instant answers powered by AI</p>
                                    </div>
                                    <i className="fas fa-chevron-right text-lg"></i>
                                </button>

                                {/* WhatsApp Option */}
                                <button
                                    onClick={() => setChatMode('whatsapp')}
                                    className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white p-5 rounded-xl hover:shadow-lg transition-all transform hover:scale-[1.02] flex items-center gap-4"
                                >
                                    <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                                        <i className="fab fa-whatsapp text-2xl"></i>
                                    </div>
                                    <div className="text-left flex-1">
                                        <p className="font-bold text-base">WhatsApp</p>
                                        <p className="text-xs opacity-90 mt-0.5">Chat with our support team</p>
                                    </div>
                                    <i className="fas fa-chevron-right text-lg"></i>
                                </button>
                            </div>
                        )}

                        {/* Live Chat Mode */}
                        {chatMode === 'live' && (
                            <>
                                <div className="relative z-10 flex-1 p-4 overflow-y-auto flex flex-col gap-3">
                                    {messages.map((msg) => (
                                        <div
                                            key={msg.id}
                                            className={`${
                                                msg.type === 'user'
                                                    ? 'bg-indigo-600 text-white p-3 rounded-2xl rounded-tr-md shadow-sm max-w-[85%] self-end'
                                                    : msg.type === 'error'
                                                    ? 'bg-red-50 text-red-800 p-3 rounded-2xl rounded-tl-md shadow-sm max-w-[85%] self-start border border-red-200'
                                                    : 'bg-white p-3 rounded-2xl rounded-tl-md shadow-sm max-w-[85%] self-start text-left border border-gray-100'
                                            }`}
                                        >
                                            <p className="text-sm leading-relaxed" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                {msg.text}
                                            </p>
                                            <span className={`text-[10px] mt-1.5 block ${msg.type === 'user' ? 'text-indigo-200 text-right' : 'text-gray-400 text-right'}`}>
                                                {formatTime(msg.timestamp)}
                                            </span>
                                        </div>
                                    ))}
                                    {isLoading && (
                                        <div className="bg-white p-3 rounded-2xl rounded-tl-md shadow-sm max-w-[85%] self-start text-left border border-gray-100">
                                            <div className="flex items-center gap-2">
                                                <div className="flex gap-1">
                                                    <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                                    <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                                    <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                                </div>
                                                <span className="text-xs text-gray-500">Typing...</span>
                                            </div>
                                        </div>
                                    )}
                                    <div ref={messagesEndRef} />
                                </div>

                                {/* Live Chat Input */}
                                <div className="relative z-10 p-3 bg-white border-t border-gray-100">
                                    <div className="flex items-center gap-2 mb-2">
                                        <button
                                            onClick={resetChat}
                                            className="text-xs text-gray-500 hover:text-indigo-600 transition-colors flex items-center gap-1"
                                        >
                                            <i className="fas fa-arrow-left"></i>
                                            <span>Back</span>
                                        </button>
                                    </div>
                                    <form onSubmit={handleLiveChat} className="flex items-center gap-2">
                                        <input
                                            ref={inputRef}
                                            type="text"
                                            value={chatMsg}
                                            onChange={(e) => setChatMsg(e.target.value)}
                                            placeholder="Ask me anything..."
                                            disabled={isLoading}
                                            className="flex-1 p-3 rounded-xl bg-gray-50 border-none outline-none focus:ring-2 focus:ring-indigo-500 text-sm text-gray-800 placeholder-gray-400 transition-all disabled:opacity-50"
                                        />
                                        <button
                                            type="submit"
                                            disabled={!chatMsg.trim() || isLoading}
                                            className="w-11 h-11 bg-indigo-600 text-white rounded-xl flex items-center justify-center hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 disabled:bg-gray-200 disabled:shadow-none"
                                        >
                                            <i className="fas fa-paper-plane text-sm"></i>
                                        </button>
                                    </form>
                                </div>
                            </>
                        )}

                        {/* WhatsApp Mode */}
                        {chatMode === 'whatsapp' && (
                            <>
                                <div className="relative z-10 flex-1 p-4 overflow-y-auto flex flex-col gap-3">
                                    <div className="bg-white p-3 rounded-lg rounded-tl-none shadow-sm max-w-[85%] self-start text-left border border-gray-100">
                                        <p className="text-sm text-gray-800">Hi there! 👋</p>
                                        <p className="text-sm text-gray-800 mt-1">Type your message below and we'll redirect you to WhatsApp to continue the conversation with our support team.</p>
                                        <span className="text-[9px] text-gray-400 mt-1 block text-right">Just now</span>
                                    </div>

                                    {chatMsg && (
                                        <div className="bg-green-50 p-3 rounded-lg rounded-tr-none shadow-sm max-w-[85%] self-end text-left border border-green-100">
                                            <p className="text-sm text-gray-800">{chatMsg}</p>
                                            <span className="text-[9px] text-gray-400 mt-1 block text-right">Ready to send</span>
                                        </div>
                                    )}
                                </div>

                                {/* WhatsApp Input */}
                                <div className="relative z-10 p-3 bg-white border-t border-gray-100">
                                    <div className="flex items-center gap-2 mb-2">
                                        <button
                                            onClick={resetChat}
                                            className="text-xs text-gray-500 hover:text-green-600 transition-colors flex items-center gap-1"
                                        >
                                            <i className="fas fa-arrow-left"></i>
                                            <span>Back</span>
                                        </button>
                                    </div>
                                    <form onSubmit={handleWhatsAppChat} className="flex items-center gap-2">
                                        <input
                                            ref={inputRef}
                                            type="text"
                                            value={chatMsg}
                                            onChange={(e) => setChatMsg(e.target.value)}
                                            placeholder="Type your message..."
                                            className="flex-1 p-3 rounded-xl bg-gray-50 border-none outline-none focus:ring-2 focus:ring-green-500 text-sm text-gray-800 placeholder-gray-400 transition-all"
                                        />
                                        <button
                                            type="submit"
                                            disabled={!chatMsg.trim()}
                                            className="w-11 h-11 bg-green-600 text-white rounded-xl flex items-center justify-center hover:bg-green-700 transition-all shadow-lg shadow-green-200 disabled:bg-gray-200 disabled:shadow-none"
                                        >
                                            <i className="fab fa-whatsapp text-sm"></i>
                                        </button>
                                    </form>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="bg-white py-2 px-4 text-center border-t border-gray-50">
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Premium Support</p>
                    </div>
                </div>
            )}

            {/* Main Toggle Button - Premium Indigo/Royal Blue */}
            <button
                onClick={() => {
                    setIsChatOpen(!isChatOpen);
                    setShowTooltip(false);
                }}
                className={`w-14 h-14 rounded-full shadow-2xl transition-all duration-500 transform hover:scale-110 flex items-center justify-center group relative z-[100000] border-2 border-white/20 ${isChatOpen ? 'bg-red-500 rotate-90' : 'bg-indigo-600 hover:bg-indigo-700 animate-pulse-subtle'}`}
                title="Support Chat"
            >
                {/* Pulsing Glow Effect */}
                {!isChatOpen && (
                    <span className="absolute inset-0 rounded-full bg-indigo-500 animate-ping opacity-20"></span>
                )}
                
                <i className={`fas ${isChatOpen ? 'fa-times' : 'fab fa-whatsapp'} text-2xl text-white relative z-10`}></i>
                
                {/* Tooltip */}
                {(showTooltip && !isChatOpen) && (
                    <div className="absolute right-full mr-4 bg-gray-900 text-white text-[12px] font-bold px-4 py-2 rounded-xl shadow-2xl border border-white/10 animate-fade-in-right whitespace-nowrap">
                        <div className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                            <span>Chat with RedBot!</span>
                        </div>
                        {/* Tooltip Arrow */}
                        <div className="absolute top-1/2 -right-1 -translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
                    </div>
                )}

                {!isChatOpen && !showTooltip && (
                    <span className="absolute right-full mr-4 bg-gray-900/95 backdrop-blur-md text-white text-[10px] font-bold uppercase tracking-wider px-3 py-2 rounded-xl opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap shadow-2xl border border-white/10 pointer-events-none">
                        {t('chatWithUs') || 'Chat with Us'}
                    </span>
                )}
            </button>
        </div>
    );
};

export default SupportChat;
